import { vec2 } from 'gl-matrix';

/**
 * Triangulates a convex polygon whose corners are given in any order.\
 * The corners are sorted by their angle about the polygon's centroid, and
 * triangles are formed between consecutive corners and the last one.
 * @param vertexCoordinates x, y of each corner.
 * @param startIndex Index of the first corner in the index buffer the
 *     result goes into, for polygons that share one buffer.
 * @returns Indices forming primitives of type {@link WebGL2RenderingContext.TRIANGLES}.
 *     A polygon of one or two corners gives a single degenerate triangle, an empty one gives no indices.
 */
export function tesselateConvexPolygon(
    vertexCoordinates: Float32Array,
    startIndex: number
): number[] {
    const numberOfPoints = vertexCoordinates.length / 2;
    if (numberOfPoints > 2) {
        const total = vec2.create();
        let i = 0;
        while (i < vertexCoordinates.length) {
            total[0] += vertexCoordinates[i++];
            total[1] += vertexCoordinates[i++];
        }
        const centroid = vec2.scale(vec2.create(), total, 1 / numberOfPoints);

        const points: { orientation: number; index: number }[] = new Array<{
            orientation: number;
            index: number;
        }>(numberOfPoints);
        //calculate orientation of each index
        i = 0;
        let index = 0;
        while (i < vertexCoordinates.length) {
            const x = vertexCoordinates[i++] - centroid[0];
            const y = vertexCoordinates[i++] - centroid[1];
            //I tried optimized atan2 https://mazzo.li/posts/vectorized-atan2.html, seems javascript's default is equally fast. WASM might be faster
            points[index] = {
                orientation: Math.atan2(y, x),
                index: startIndex + index,
            };
            ++index;
        }
        points.sort(
            (
                a: { orientation: number; index: number },
                b: { orientation: number; index: number }
            ): number => {
                return a.orientation - b.orientation;
            }
        );
        const indices = new Array<number>((numberOfPoints - 2) * 3);
        const last = points[numberOfPoints - 1];
        let currentIndex = 0;
        for (let a = 1, b = numberOfPoints - 1; a < b; ++a) {
            indices[currentIndex++] = points[a - 1].index;
            indices[currentIndex++] = points[a].index;
            indices[currentIndex++] = last.index;
        }

        return indices;
    } else if (numberOfPoints > 0) {
        return [
            startIndex,
            startIndex + (numberOfPoints === 2 ? 1 : 0),
            startIndex,
        ];
    }
    return [];
}
